/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';
import { LONG_MONTH_YEAR_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Grid, Tag, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { LinkIcon } from '@coursera/cds-icons';
import Link from '@coursera/event-pulse/react/Link';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import { ChallengeAchievement } from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/ChallengeAchievement';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import type { SharedLearnerProfile_PortfolioProjectsFragment as LearnerProfilePortfolioProject } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/SharedLearnerProfile_portfolioProjects';
import { getViewProjectDialogTranslations } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/projectUtils';
import { CMLOrHTMLV2 } from 'bundles/cml';
import { useEnableProjectEnhancements } from 'bundles/employability-common/feature-flags/hooks/useEnableProjectEnhancements';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const styles = {
  summaryContainer: css`
    border: 2px solid var(--cds-color-grey-50);
    border-radius: 8px;
    padding: var(--cds-spacing-300);

    ${breakpoints.between('xs', 'md')} {
      margin-left: 0;
      padding: var(--cds-spacing-150);
    }

    ${breakpoints.between('xs', 'sm')} {
      margin-top: var(--cds-spacing-600);
      padding: var(--cds-spacing-150);
    }
  `,
  withSmallBottomMargin: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  withBottomMargin: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  withMarginRight: css`
    margin-right: var(--cds-spacing-200);
  `,
  linkContainer: css`
    display: flex;
    align-items: center;
    background-color: var(--cds-color-blue-50);
    padding: var(--cds-spacing-200);
  `,
  cmlOverride: css`
    /* This is a bit hacky, but it's needed because when the CML renders, there's a margin-top added that's interfering with designs
    Here's a screenshot of what it would look like without this: https://drive.google.com/file/d/1uWXsQKz5IV_MK84sn06RHefnaAv2hbRM/view?usp=sharing */
    [data-testid='cml-viewer'] > p {
      margin-top: 0;
    }
  `,
  italicize: css`
    font-style: italic;
  `,
  artifactLink: css`
    margin-top: var(--cds-spacing-200);
  `,
};

type PropsFromCaller = {
  currentProject: LearnerProfilePortfolioProject;
};

export const ViewProjectDialogDetails = ({ currentProject }: PropsFromCaller) => {
  const translations = getViewProjectDialogTranslations();

  const { profileState = {}, sharedEventingV3Data, editable } = useContext(ProfileContext);
  const enableProjectEnhancements = useEnableProjectEnhancements();

  const { fullName = '' } = profileState;

  const {
    id,
    skills,
    linkToWork,
    tools,
    businessChallenge,
    challengeAchievement,
    completed,
    artifacts,
    summary,
    approach,
    solution,
  } = currentProject;

  const retrackedTrack = useRetracked();

  // eslint-disable-next-line camelcase
  const projectTrackingData = { project_id: id };

  const handleArtifactLinkClick = () => {
    retrackedTrack({
      trackingName: EVENTS.profileProjectArtifactLink,
      trackingData: projectTrackingData,
      action: 'click',
    });
  };

  const showProjectEnhancements = enableProjectEnhancements && editable;
  const showSummary = showProjectEnhancements || !!summary;
  const showSolution = showProjectEnhancements || !!solution;
  const showApproach = showProjectEnhancements || !!approach;

  return (
    <Grid
      key={currentProject.id} // This is needed to force a re-render when the project changes, so events are tracked correctly
      component={TrackedDiv}
      withVisibilityTracking
      requireFullyVisible={false}
      // not tracked in eventing v3
      trackingName="view_projects_dialog"
      data={{
        ...projectTrackingData,
        projectSlug: currentProject.slug,
      }}
      trackClicks={false}
      container
      direction="row"
      justifyContent="space-between"
    >
      <Grid container item xs={12} sm={4} direction="column">
        <Typography2 component="h3" variant="subtitleMedium" css={styles.withSmallBottomMargin}>
          {translations.projectScenarioTitle}
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary" color="supportText" css={styles.withBottomMargin}>
          {businessChallenge}
        </Typography2>

        {skills.length > 0 && (
          <>
            <Typography2 component="h3" variant="subtitleMedium" css={styles.withSmallBottomMargin}>
              {translations.skillsDemonstratedTitle}
            </Typography2>
            <div css={styles.withBottomMargin}>
              {skills.map((tag) => (
                <Tag
                  onClick={() => {
                    // Wrapping the tag in a <TrackedDiv> butchers the styling, so I'm using the hook since it's just as effective
                    // and simpler to implement
                    retrackedTrack({
                      // not tracked in eventing V3
                      trackingName: 'view_project_skill_tag',
                      trackingData: {
                        ...projectTrackingData,
                        skill: tag,
                      },
                      action: 'click',
                    });
                  }}
                  variant="category"
                  data-testid="skill-tag"
                  key={tag}
                  priority="primary"
                  css={[styles.withSmallBottomMargin, styles.withMarginRight]}
                >
                  {tag}
                </Tag>
              ))}
            </div>
          </>
        )}

        {tools?.length > 0 && (
          <>
            <Typography2 component="h3" variant="subtitleMedium" css={styles.withSmallBottomMargin}>
              {translations.toolsUsedTitle}
            </Typography2>
            <div css={styles.withBottomMargin}>
              {tools.map((tool) => (
                <Tag
                  onClick={() => {
                    // Wrapping the tag in a <TrackedDiv> butchers the styling, so I'm using the hook since it's just as effective
                    // and simpler to implement
                    retrackedTrack({
                      // not tracked in eventing v3
                      trackingName: 'view_project_tool_tag',
                      trackingData: {
                        ...projectTrackingData,
                        tool,
                      },
                      action: 'click',
                    });
                  }}
                  data-testid="tool-tag"
                  key={tool}
                  variant="category"
                  priority="primary"
                  css={styles.withMarginRight}
                >
                  {tool}
                </Tag>
              ))}
            </div>
          </>
        )}

        {challengeAchievement && (
          <ChallengeAchievement
            learnerName={fullName}
            achievement={challengeAchievement}
            label={
              <Typography2 component="h3" variant="subtitleMedium" css={styles.withSmallBottomMargin}>
                {_t('Ranking')}
              </Typography2>
            }
          />
        )}
      </Grid>
      <Grid container item xs={12} sm={7} direction="column" css={styles.summaryContainer}>
        {showSummary && (
          <>
            <Typography
              variant="h2semibold"
              css={styles.withSmallBottomMargin}
              color={!summary ? 'supportText' : 'body'}
            >
              {translations.summaryTitle}
            </Typography>
            <Typography2
              component="p"
              variant="bodyPrimary"
              color={!summary ? 'supportText' : 'body'}
              css={[styles.cmlOverride, styles.withBottomMargin, !summary && styles.italicize]}
            >
              {summary || translations.summaryPlaceholder}
            </Typography2>
          </>
        )}

        {showSolution && (
          <>
            <Typography
              variant="h2semibold"
              css={styles.withSmallBottomMargin}
              color={!solution ? 'supportText' : 'body'}
            >
              {translations.solutionTitle}
            </Typography>
            <Typography2
              component="p"
              color={!solution ? 'supportText' : 'body'}
              variant="bodyPrimary"
              css={[styles.cmlOverride, styles.withBottomMargin, !solution && styles.italicize]}
            >
              {/* @ts-expect-error - we don't use htmlWithMetadata and including it creates graphql errors */}
              {solution ? <CMLOrHTMLV2 value={solution} /> : translations.solutionPlaceholder}
            </Typography2>
          </>
        )}

        {showApproach && (
          <>
            <Typography
              color={!approach ? 'supportText' : 'body'}
              variant="h2semibold"
              css={styles.withSmallBottomMargin}
            >
              {translations.approachTitle}
            </Typography>
            <Typography2
              component="p"
              color={!approach ? 'supportText' : 'body'}
              variant="bodyPrimary"
              css={[styles.cmlOverride, styles.withBottomMargin, !approach && styles.italicize]}
            >
              {/* @ts-expect-error - we don't use htmlWithMetadata and including it creates graphql errors */}
              {approach ? <CMLOrHTMLV2 value={approach} /> : translations.approachPlaceholder}
            </Typography2>
          </>
        )}

        {!enableProjectEnhancements && linkToWork && (
          <>
            <Typography variant="h2semibold" css={styles.withSmallBottomMargin}>
              {translations.projectLinksTitle}
            </Typography>
            <TrackedDiv
              css={[styles.linkContainer, styles.withBottomMargin]}
              trackClicks={false}
              withVisibilityTracking
              data={projectTrackingData}
              trackingName={EVENTS.profileProjectArtifactLink}
              requireFullyVisible={false}
            >
              <Link
                trackingData={{
                  button: {
                    name: EVENTS.profileProjectArtifactLink,
                    linkType: 'external' as const,
                    linkURL: linkToWork,
                  },
                  product: {
                    id,
                  },
                  ...sharedEventingV3Data,
                }}
                onClick={handleArtifactLinkClick}
                href={linkToWork}
                rel="noopener noreferrer"
                target="_blank"
                variant="standard"
                typographyVariant="bodyPrimary"
                iconPosition="before"
                icon={<LinkIcon size="large" />}
              >
                {linkToWork}
              </Link>
            </TrackedDiv>
          </>
        )}

        {enableProjectEnhancements && artifacts && artifacts.length > 0 && (
          <>
            <Typography variant="h2semibold" css={styles.withSmallBottomMargin}>
              {translations.projectLinksTitle}
            </Typography>
            <TrackedDiv
              css={[styles.withBottomMargin]}
              trackClicks={false}
              withVisibilityTracking
              data={projectTrackingData}
              trackingName={EVENTS.profileProjectArtifactLink}
              requireFullyVisible={false}
            >
              {artifacts.map((artifact) => (
                <Link
                  css={[styles.linkContainer, styles.artifactLink]}
                  trackingData={{
                    button: {
                      name: EVENTS.profileProjectArtifactLink,
                      linkType: 'external' as const,
                      // @ts-expect-error URL: string
                      linkURL: artifact.link,
                    },
                    product: {
                      id,
                    },
                    ...sharedEventingV3Data,
                  }}
                  onClick={handleArtifactLinkClick}
                  // @ts-expect-error URL: string
                  href={artifact.link}
                  rel="noopener noreferrer"
                  target="_blank"
                  variant="standard"
                  typographyVariant="bodyPrimary"
                  iconPosition="before"
                  icon={<LinkIcon size="large" />}
                >
                  {artifact.type || artifact.link}
                </Link>
              ))}
            </TrackedDiv>
          </>
        )}

        {completed && (
          <Typography2 component="p" variant="bodyPrimary" color="supportText">
            <FormattedMessage
              message={_t('Completed {completionDate}')}
              completionDate={formatDateTimeDisplay(completed, LONG_MONTH_YEAR_DISPLAY)}
            />
          </Typography2>
        )}
      </Grid>
    </Grid>
  );
};

export default ViewProjectDialogDetails;
